/**
 * FeaturesPage.tsx — /#/features
 */
import { useNavigate } from 'react-router-dom'

const GOLD = '#c9a84c'
const NAVY = '#0a1628'

const sections = [
  {
    title: 'Shift Management',
    icon: '📋',
    items: [
      { name: 'Daily posting board', shift: true, terrain: true, command: true },
      { name: 'Officer-to-post assignment', shift: true, terrain: true, command: true },
      { name: 'Break scheduling + rotation', shift: true, terrain: true, command: true },
      { name: 'Supervisor + officer roles', shift: true, terrain: true, command: true },
      { name: 'Desk officer assignment', shift: true, terrain: true, command: true },
      { name: 'Shift notes + special details', shift: true, terrain: true, command: true },
      { name: 'Shift archive + history', shift: true, terrain: true, command: true },
    ],
  },
  {
    title: 'Equipment Tracking',
    icon: '📻',
    items: [
      { name: 'Radio assignment + OOS tracking', shift: true, terrain: true, command: true },
      { name: 'Security key tracking', shift: true, terrain: true, command: true },
      { name: 'Body recorder tracking', shift: true, terrain: true, command: true },
      { name: 'Access badge tracking', shift: true, terrain: true, command: true },
      { name: 'Equipment OOS dashboard', shift: true, terrain: true, command: true },
    ],
  },
  {
    title: 'Cloud & Sync',
    icon: '☁️',
    items: [
      { name: 'Real-time cloud sync', shift: true, terrain: true, command: true },
      { name: 'Multi-device support', shift: true, terrain: true, command: true },
      { name: 'Offline-capable', shift: true, terrain: true, command: true },
      { name: 'Shift change auto-carry-over', shift: true, terrain: true, command: true },
    ],
  },
  {
    title: 'Mobile App',
    icon: '📱',
    items: [
      { name: 'Android native app', shift: true, terrain: true, command: true },
      { name: 'Mobile command center', shift: true, terrain: true, command: true },
      { name: 'Officer task view', shift: true, terrain: true, command: true },
      { name: 'Shift + break end notifications', shift: true, terrain: true, command: true },
      { name: 'iOS app', shift: false, terrain: false, command: false, note: 'Coming soon' },
    ],
  },
  {
    title: 'Security & Compliance',
    icon: '🔒',
    items: [
      { name: 'JWT authentication', shift: true, terrain: true, command: true },
      { name: 'Role-based access control', shift: true, terrain: true, command: true },
      { name: 'Audit log (all actions)', shift: true, terrain: true, command: true },
      { name: 'Password reset via email', shift: true, terrain: true, command: true },
      { name: 'Session management', shift: true, terrain: true, command: true },
      { name: 'Idle auto sign-out', shift: true, terrain: true, command: true },
    ],
  },
  {
    title: 'Terrain + NFC',
    icon: '🗺️',
    items: [
      { name: 'Zone terrain map', shift: false, terrain: true, command: true },
      { name: 'NFC checkpoint scanning', shift: false, terrain: true, command: true },
      { name: 'Officer geolocation (zone-level)', shift: false, terrain: true, command: true },
      { name: 'Incident reports', shift: false, terrain: true, command: true },
    ],
  },
  {
    title: 'Command + Enterprise',
    icon: '🖥️',
    items: [
      { name: 'Command dashboard', shift: false, terrain: false, command: true },
      { name: 'Multi-site management', shift: false, terrain: false, command: true },
      { name: 'REST API access', shift: false, terrain: false, command: true },
      { name: 'Camera integration', shift: false, terrain: false, command: true },
      { name: 'Access control integration', shift: false, terrain: false, command: true },
      { name: 'Priority SLA', shift: false, terrain: false, command: true },
    ],
  },
]

const Check = ({ val, note }: { val: boolean; note?: string }) => (
  <td style={{ textAlign: 'center', padding: '10px 8px', fontSize: 14 }}>
    {note ? <span style={{ color: '#475569', fontSize: 12 }}>{note}</span>
      : val ? <span style={{ color: '#4ade80', fontSize: 18 }}>✓</span>
      : <span style={{ color: '#1e293b', fontSize: 18 }}>—</span>}
  </td>
)

export default function FeaturesPage() {
  const navigate = useNavigate()

  return (
    <div style={{ background: NAVY, color: '#e2e8f0', fontFamily: "'Segoe UI', system-ui, sans-serif", minHeight: '100vh' }}>
      <nav style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(201,168,76,0.15)', display: 'flex', alignItems: 'center', padding: '0 32px', height: 60, position: 'sticky', top: 0, zIndex: 100 }}>
        <div onClick={() => navigate('/')} style={{ fontSize: 20, fontWeight: 800, color: GOLD, letterSpacing: 2, flex: 1, cursor: 'pointer' }}>CASTELLAN</div>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
          <span onClick={() => navigate('/pricing')} style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 14 }}>Pricing</span>
          <span onClick={() => navigate('/login')} style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 14 }}>Sign in</span>
          <button onClick={() => navigate('/signup')} style={{ padding: '8px 20px', borderRadius: 8, background: GOLD, color: NAVY, fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none' }}>Get started</button>
        </div>
      </nav>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '64px 24px' }}>
        <h1 style={{ fontSize: 48, fontWeight: 800, color: '#f1f5f9', textAlign: 'center', marginBottom: 12 }}>Everything Castellan can do.</h1>
        <p style={{ textAlign: 'center', color: '#64748b', fontSize: 18, marginBottom: 56 }}>Compare features across all three tiers.</p>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#060d18' }}>
                <th style={{ textAlign: 'left', padding: '14px 16px', color: '#64748b', fontSize: 12, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Feature</th>
                {[['Shift', '$49.99'], ['Terrain+NFC', '$149.99'], ['Command', '$399.99']].map(([n, p]) => (
                  <th key={n} style={{ textAlign: 'center', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ color: GOLD, fontWeight: 700, fontSize: 13 }}>{n}</div>
                    <div style={{ color: '#475569', fontSize: 12, fontWeight: 400 }}>{p}/mo</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sections.map(section => (
                <>
                  <tr key={section.title}>
                    <td colSpan={4} style={{ padding: '20px 16px 8px', color: GOLD, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '.08em' }}>
                      {section.icon} {section.title}
                    </td>
                  </tr>
                  {section.items.map((item, i) => (
                    <tr key={item.name} style={{ background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td style={{ padding: '10px 16px', fontSize: 14, color: '#94a3b8' }}>{item.name}</td>
                      <Check val={item.shift} note={item.note} />
                      <Check val={item.terrain} note={item.note} />
                      <Check val={item.command} note={item.note} />
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ textAlign: 'center', marginTop: 56 }}>
          <button onClick={() => navigate('/signup')} style={{ padding: '16px 40px', borderRadius: 8, background: GOLD, color: NAVY, fontWeight: 700, fontSize: 16, cursor: 'pointer', border: 'none' }}>
            Start your free trial →
          </button>
          <p style={{ color: '#475569', fontSize: 13, marginTop: 12 }}>14 days free on Shift. No credit card required.</p>
        </div>
      </div>
    </div>
  )
}
